"use client";

import { EventTrackingProps, L1ApopProps } from "@/types/common";
import { Toast } from "@monsterindia/global-ui-components";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import ErrorBoundary from "@/components/common/errorBoundary";
import { fetchUserJobInfoAPI } from "@/components/dashboard/query";

import { ColumnType, recommendedPageLayoutConfig } from "../config";
import { useDashboardContext } from "../index";
import { recommendedPageMap } from "../map";
import "../recommended/recommendedJobCard/components/styles.css";
import { RouteLeaveGuard } from "../routeLeaveGuard";
import { deleteMPCookies } from "../utils/deleteMPCookies";
import { getCookieDomain } from "../utils/getCookieDomain";
import { getStartCase } from "../utils/getStartCase";
import resolveABVariant from "../utils/resolveABVariant";
import MultiSelectDiscard from "./multiSelectDiscard";
import {
  RecommendedDashboardPageDataType,
  RecommendedJobAPIDataType,
  RecommendedPageContextProps,
  RecommendedPageDataType,
} from "./types";

const Apop = dynamic(() =>
  import("@monsterindia/l1-apop").then(
    (mod) => mod.Apop as unknown as React.FC<L1ApopProps>,
  ),
);
interface RecommendedPageProps {
  setRecommendedDashboardData: (data: RecommendedDashboardPageDataType) => void;
}

const RecommendedPageContext = createContext<RecommendedPageContextProps>({
  recommendedJobData: {} as RecommendedPageDataType,
  setRecommendedJobData: (data: RecommendedPageDataType) => {},
  handleEventTracking: (event: EventTrackingProps) => {},
  jobUserInfoHandler: () => {},
  cleverTapPropertyValues: {
    applied: "",
    recommended: "",
    saved: "",
  },
});

export function useRecommendedPageData() {
  return useContext(RecommendedPageContext);
}

const RecommendedPage: React.FC<RecommendedPageProps> = ({
  setRecommendedDashboardData,
}) => {
  const {
    isMobile,
    jobServerData,
    handleEventTracking,
    cleverTapPropertyValues,
  } = useDashboardContext();
  const searchParams = useSearchParams();

  const [recommendedJobData, setRecommendedJobData] =
    useState<RecommendedPageDataType>(() => {
      let totalDataLength = 0;
      let totalPagingLength = 0;
      let completeLinearData: RecommendedJobAPIDataType[] = [];
      const filterName: string[] = [];
      for (const job in jobServerData.jobData) {
        if (jobServerData.jobData?.[job]?.data?.length > 0) {
          filterName.push(job);
          totalDataLength = jobServerData.jobData[job]?.data?.length || 0;
          totalPagingLength =
            jobServerData.jobData[job]?.meta?.paging?.total ||
            jobServerData.jobData[job]?.data?.length ||
            0;
          completeLinearData = [
            ...completeLinearData,
            ...(jobServerData.jobData?.[job]?.data ?? []),
          ];
          break;
        }
      }
      completeLinearData =
        completeLinearData?.map((job: RecommendedJobAPIDataType) => {
          return {
            ...job,
            isApplied: false,
            multiSelectData: {
              configureData: {
                fetch: false,
                selected: false,
                reset: false,
              },
            },
          };
        }) || [];

      const recommendedJobs: RecommendedPageDataType = {
        data: jobServerData.jobData,
        totalDataLength: totalDataLength || 0,
        totalPagingLength: totalPagingLength || 0,
        siteProps: jobServerData.siteProps,
        sitePropsProperties: jobServerData.sitePropsProperties,
        filterStatusPresent: totalDataLength || false,
        savedJobs: [],
        profilePersonalDetails: jobServerData.profilePersonalDetails,
        isLoadingJobDesc: true,
        jobDescIndex: { selectedIndex: isMobile ? undefined : 0 },
        showMobileModal: false,
        autoApplyData: jobServerData.autoApplyData,
        similarJobsSelected: false,
        activeTab: jobServerData.activeTab,
        newDataLoader: jobServerData.clientTabSwitch,
        toastMessage: { text: "", type: "", show: false },
        displayRecommendedData: completeLinearData,
        multiSelectCount: 0,
        validateProfileData: jobServerData.validateProfileData,
        outreachSubscriptionData: jobServerData.outreachSubscriptionData || {},
        userInfo: { appliedJobIds: [], savedJobIds: [] },
        abTestBulkApply: "success",
        dashboardHeaderRef: jobServerData.dashboardHeaderRef,
        windowData: {
          recaptchaV2Switch:
            jobServerData?.windowData?.recaptchaV2Switch || false,
          isSyndicateQuickApplyEnabled:
            jobServerData?.windowData?.isSyndicateQuickApplyEnabled || false,
          bulkApplyMultiSelect:
            jobServerData?.windowData?.bulkApplyMultiSelect || "",
        },
        selectedFilter: [
          {
            id: filterName[0],
            selected: true,
            text: filterName[0],
            count:
              jobServerData.jobData?.[filterName[0]]?.meta?.paging?.total ||
              totalDataLength ||
              10,
          },
        ],
        selectFilterFetch: "server",
      };
      return recommendedJobs;
    });

  const [loader, setLoader] = useState(true);
  const handleBulkApplyApiRetry = async () => {
    try {
      const res = await fetch(`/home/api/bulkJobApply?utm_source=bulk_apply`, {
        method: "POST",
        body: JSON.stringify({
          jobs: (recommendedJobData.multiSelectJobIds ?? [])
            .filter((jobId: number) => jobId)
            .map((jobId: number) => {
              return {
                jobId: jobId,
              };
            }),
        }),
      });

      const response = await res.json();
      const statusSuccess = response.status !== 400 ? "success" : "failed";
      setRecommendedJobData((prevData: any) => {
        const modifiedData = { ...prevData };
        modifiedData.totalSuccessfulBulkApplyData = {
          fetchDone: statusSuccess,
          totalSuccessfulBulkApply:
            statusSuccess === "success" ? modifiedData.multiSelectCount : 0,
          totalBulkApply: modifiedData.multiSelectCount,
        };
        modifiedData.nudgeData = undefined;
        modifiedData.popUpOpened = false;
        return modifiedData;
      });
    } catch {
      setRecommendedJobData((prevData: any) => {
        const modifiedData = { ...prevData };
        modifiedData.totalSuccessfulBulkApplyData = {
          fetchDone: "failed",
          totalSuccessfulBulkApply: 0,
          totalBulkApply: modifiedData.multiSelectCount,
        };
        modifiedData.nudgeData = undefined;
        modifiedData.popUpOpened = false;
        return modifiedData;
      });
    }
  };

  const jobUserInfoHandler = useCallback(
    async (jobs: RecommendedJobAPIDataType[]) => {
      const jobDataIds = jobs?.map((job: RecommendedJobAPIDataType) => job.id);
      const response = await fetchUserJobInfoAPI(jobDataIds);
      setRecommendedJobData((prevData: RecommendedPageDataType) => {
        const recommendedJobs = {
          ...prevData,
          userInfo: response,
          savedJobs: (response?.savedJobIds || []).map(Number),
        };
        const completeLinearData =
          recommendedJobs.displayRecommendedData?.map(
            (job: RecommendedJobAPIDataType) => {
              return {
                ...job,
                isApplied:
                  response?.appliedJobIds?.includes(job?.id || "") || false,
              };
            },
          ) || [];

        recommendedJobs.displayRecommendedData = [...completeLinearData];
        return recommendedJobs;
      });
    },
    [isMobile],
  );

  const layout = useMemo(() => {
    if (!isMobile) {
      return recommendedPageLayoutConfig.layouts.desktop;
    }
    return recommendedPageLayoutConfig.layouts.mobile;
  }, [isMobile]);

  const contextValue = useMemo(
    () => ({
      recommendedJobData,
      setRecommendedJobData,
      handleEventTracking,
      jobUserInfoHandler,
      cleverTapPropertyValues,
    }),
    [recommendedJobData, handleEventTracking, jobUserInfoHandler],
  );

  const onApopComplete = async () => {
    const bulkApplyStatus =
      recommendedJobData?.totalSuccessfulBulkApplyData?.fetchDone ?? null;
    if (bulkApplyStatus === "failed") {
      await handleBulkApplyApiRetry();
    } else {
      setRecommendedJobData((prevData: RecommendedPageDataType) => {
        const savedJobsModified = {
          ...prevData,
        };
        savedJobsModified.nudgeData = undefined;
        savedJobsModified.popUpOpened = false;
        return savedJobsModified;
      });
    }
  };
  const onApopClose = () => {
    setRecommendedJobData((prevData: RecommendedPageDataType) => {
      const savedJobsModified = {
        ...prevData,
      };
      savedJobsModified.nudgeData = undefined;
      savedJobsModified.popUpOpened = false;
      return savedJobsModified;
    });
  };

  const resolveABVariantFun = () => {
    const email =
      recommendedJobData?.profilePersonalDetails?.personalDetailSection
        ?.personalDetails?.emails[0].email || "";
    const data = {
      experimentName: "MultiApplyExperiment",
      id: email,
    };
    resolveABVariant(
      data,
      ["BULK_APPLY_FLOW"],
      () => {
        setRecommendedJobData((prevData: RecommendedPageDataType) => {
          const recommendedJobs = {
            ...prevData,
            abTestBulkApply: "success",
          };
          return recommendedJobs;
        });
        handleEventTracking({
          eventName: "BulkApplyExperiment",
          eventType: "eventPush",
          eventProps: {
            type: "bulk",
            page_name:
              cleverTapPropertyValues[
                recommendedJobData.activeTab as keyof typeof cleverTapPropertyValues
              ],
          },
        });
      },
      () => {
        setRecommendedJobData((prevData: RecommendedPageDataType) => {
          const recommendedJobs = {
            ...prevData,
            abTestBulkApply: "failure",
          };
          return recommendedJobs;
        });
        handleEventTracking({
          eventName: "BulkApplyExperiment",
          eventType: "eventPush",
          eventProps: {
            type: "regular",
            page_name:
              cleverTapPropertyValues[
                recommendedJobData.activeTab as keyof typeof cleverTapPropertyValues
              ],
          },
        });
      },
    );
  };

  useEffect(() => {
    if (jobServerData.clientTabSwitch) {
      const params = new URLSearchParams(window.location.search);
      const activeTabClient = params.get("activeTab");
      if (
        jobServerData.activeTab === activeTabClient &&
        jobServerData.activeTab === "recommended"
      ) {
        resolveABVariantFun();
      }
    }
  }, [jobServerData, searchParams]);

  useEffect(() => {
    setRecommendedDashboardData({
      multiSelectCount: recommendedJobData.multiSelectCount || 0,
    });
  }, [recommendedJobData.multiSelectCount]);

  useEffect(() => {
    //Bulk Apply AB Test
    // resolveABVariantFun();
    handleEventTracking({
      eventName: "BulkApplyExperiment",
      eventType: "eventPush",
      eventProps: {
        type: "bulk",
        page_name:
          cleverTapPropertyValues[
            recommendedJobData.activeTab as keyof typeof cleverTapPropertyValues
          ],
      },
    });
    jobUserInfoHandler(recommendedJobData?.displayRecommendedData || []);

    setTimeout(() => {
      setLoader(false);
    }, 0);

    handleEventTracking({
      eventName: "Pageviewed",
      eventType: "eventPush",
      eventProps: {
        page_name:
          cleverTapPropertyValues[
            recommendedJobData.activeTab as keyof typeof cleverTapPropertyValues
          ],
      },
    });
    const cookieHost = getCookieDomain();
    deleteMPCookies(cookieHost);
  }, []);

  useEffect(() => {
    setRecommendedJobData((prevData: RecommendedPageDataType) => {
      const recommendedJobsModified = {
        ...prevData,
      };
      if (isMobile)
        recommendedJobsModified["jobDescIndex"] = { selectedIndex: 0 };
      return recommendedJobsModified;
    });
  }, [isMobile]);

  useEffect(() => {
    setRecommendedJobData((prevData: RecommendedPageDataType) => {
      const recommendedJobsModified = {
        ...prevData,
        windowData: {
          recaptchaV2Switch:
            window?._siteProps_?.propertiesMap?.recaptchaV2Switch || false,
          isSyndicateQuickApplyEnabled:
            window?._siteProps_?.propertiesMap?.syndicatedQuickApplyEnabled ||
            false,
          bulkApplyMultiSelect:
            window?._siteProps_?.propertiesMap?.bulkApplyMultiSelect || "",
        },
      };
      return recommendedJobsModified;
    });
  }, []);

  const RenderComponent = ({ name }: { name: DashboardSectionKey }) => {
    const Component = recommendedPageMap[name];

    if (!Component) {
      return null;
    }

    return (
      <ErrorBoundary
        message={`Failed to load ${name.toLowerCase().replace(/_/g, " ")}`}
      >
        <Component />
      </ErrorBoundary>
    );
  };

  const renderSection = useCallback(
    (section: ColumnType[]) => {
      return section.map((row, rowIndex) => (
        <div key={rowIndex} className="flex w-full" id={row?.row[0]?.component}>
          {row.row.map((item, itemIndex) => (
            <div
              key={itemIndex}
              className="w-full"
              style={{
                ...item.styles,
                width: item.width,
                padding: item.padding,
              }}
            >
              <RenderComponent name={item.component} />
            </div>
          ))}
        </div>
      ));
    },
    [isMobile],
  );
  const renderRightcolumn = <div>{renderSection(layout.rightColumn)}</div>;
  return (
    <>
      {recommendedJobData.nudgeData && (
        <Apop
          data={recommendedJobData.nudgeData}
          openApop={recommendedJobData.nudgeData && true}
          closeApop={onApopClose}
          flowType={
            recommendedJobData.nudgeData.actionType ===
              "UPDATE_RESUME_AND_EMPLOYMENT" ||
            recommendedJobData.nudgeData.actionType === "UPDATE_WORK_AUTH"
              ? "PROFILE_NUDGE"
              : "L1_APOP"
          }
          showEmailVerification={false}
          type={recommendedJobData.nudgeData.actionType}
          onApopComplete={onApopComplete}
          isRecaptchaEnabled={
            recommendedJobData.windowData?.recaptchaV2Switch ?? false
          }
          callOnCompleteBeforeClose={true}
        />
      )}
      <RouteLeaveGuard
        when={recommendedJobData?.multiSelectCount > 0 || false}
        message="Are you sure?"
      />
      <Toast
        timer={3000}
        showToast={recommendedJobData?.toastMessage?.show}
        text={recommendedJobData?.toastMessage?.text}
        type={recommendedJobData?.toastMessage?.type}
        onClose={() => {
          return setRecommendedJobData((prevData: RecommendedPageDataType) => {
            return {
              ...prevData,
              toastMessage: { text: "", type: "", show: false },
            };
          });
        }}
        width={isMobile ? "75" : "30"}
        position="top-center"
      />
      <RecommendedPageContext.Provider value={contextValue}>
        {recommendedJobData?.discardBulkApplyPopup?.value ? (
          <MultiSelectDiscard />
        ) : null}
        <div className={`${loader ? "hidden" : ""}`}>
          <div
            className="box-border flex h-full w-auto flex-col md:m-auto md:!flex-row md:items-start md:!gap-6"
            id="recommended-dashboard-content"
          >
            {layout.leftColumn && (
              <div
                className="sticky z-[1001] md:!z-20 md:flex md:!w-[288px] md:shrink-0 md:grow-0"
                id="recommended-dashboard-filters"
              >
                {renderSection(layout.leftColumn)}
              </div>
            )}
            {layout.middleColumn && (
              <div className={isMobile ? "w-full px-4 pb-10" : "md:!w-[560px]"}>
                {renderSection(layout.middleColumn)}
              </div>
            )}
            {layout.rightColumn && (!isMobile ? renderRightcolumn : "")}
          </div>
        </div>
      </RecommendedPageContext.Provider>
    </>
  );
};

export default React.memo(RecommendedPage);
